/** Privacy and guardrails in plain words (ADR-0064): presets, explanations, tester, per agent. */
export default {
  en: {
    intro: "Decide what happens when a message contains personal data, secrets or risky content. Questions are checked before they reach the model; answers before they reach the person.",
    scope: "Rules for",
    scopeOrg: "The whole organisation",
    scopeAgent: "Agent: {{name}}",
    agentInherits: "{{name}} follows the organisation's rules. Saving here gives it its own rules.",
    agentOwn: "{{name}} has its own rules.",
    reset: "Use the organisation's rules",
    resetTitle: "Remove this agent's own rules? It will follow the organisation's rules again.",
    resetDone: "{{name}} follows the organisation's rules again",
    cancel: "Cancel",
    presetTitle: "How careful",
    preset: { strict: "Strict", balanced: "Balanced", lenient: "Lenient", custom: "Custom" },
    presetHelp: {
      strict: "Catches the most. Hides personal data even when unsure and stops anything that looks risky. Some harmless messages may be stopped.",
      balanced: "Recommended. Hides personal data and secrets, stops attempts to trick the agent and offensive content.",
      lenient: "Fewer interruptions. Only clear cases are stopped; personal data in questions is noted rather than hidden.",
      custom: "These rules were changed by hand (see Advanced). Pick a level to replace them.",
    },
    whatHappens: "What happens",
    colWhat: "When a message contains…",
    direction: { input: "Questions", output: "Answers" },
    outcome: { block: "Stopped", redact: "Hidden", flag: "Noted only", off: "Allowed" },
    category: {
      pii: { name: "Personal data", explain: "Names with ID numbers, phone numbers, e-mail addresses, IBANs, card numbers." },
      secret: { name: "Secrets", explain: "Passwords, API keys and access tokens." },
      prompt_injection: { name: "Attempts to trick the agent", explain: "Text that tries to override the agent's instructions, for example “ignore your rules”." },
      unsafe_action: { name: "Risky requests", explain: "Requests to run commands, delete data or move money outside the normal steps." },
      toxicity: { name: "Offensive content", explain: "Insults, threats and hateful language, in Arabic or English." },
    },
    advanced: "Advanced: actions and sensitivity per category",
    selfCheck: "Extra check by an AI model (slower)",
    colCategory: "Category",
    colAction: "Action",
    colThreshold: "Sensitivity (0–1)",
    thresholdHelp: "Sensitivity is the confidence needed before the action applies: lower values catch more, higher values catch only clear cases.",
    save: "Save the rules",
    saveAgent: "Save the rules for {{name}}",
    saved: "Rules saved",
    history: "Changes",
    noHistory: "No changes yet: the platform defaults apply.",
    colVersion: "Version",
    colBy: "Changed by",
    colAt: "When",
    initialSetup: "Initial setup",
    showUnchanged_one: "Show {{count}} save that changed nothing",
    showUnchanged_other: "Show {{count}} saves that changed nothing",
    unchangedAt: "{{at}} (no change)",
    technical: "Technical details",
    test: {
      title: "Test with sample text",
      help: "See what these rules (including unsaved changes) would do. Nothing is stored; the sample is not kept.",
      direction: "Check the text as",
      as: { input: "A question to the agent", output: "An answer from the agent" },
      sample: "Sample text",
      placeholder: "For example: My e-mail is sara@example.com and my IBAN is SA44 2000 0001 2345 6789 1234",
      run: "Test",
      decision: { allow: "Would pass unchanged", redact: "Would pass with some parts hidden", block: "Would be stopped" },
      nothing: "Nothing was found.",
      found: "What was found",
      redacted: "What the model or person would see",
    },
    issues: {
      redact_only_pii_secret: "Only personal data and secrets can be hidden; choose Stopped, Noted only or Allowed for the others.",
      threshold_range: "Sensitivity must be between 0 and 1.",
      own_name: "Give the guardrail a name in English and in Arabic.",
      own_phrases: "Add at least one word or phrase.",
      own_phrases_long: "Each word or phrase can have at most 100 characters, and there can be at most 200.",
      own_topic: "Describe the topic in a few words (at most 300 characters).",
      own_pattern: "Write a pattern (at most 200 characters).",
      own_pattern_invalid: "This pattern is not valid. Check the brackets and special characters.",
      own_pattern_empty: "This pattern also matches empty text, so it would match everywhere.",
      own_topic_hide: "A topic cannot be hidden: choose Stopped or Noted only.",
    },
    own: {
      title: "Your own guardrails",
      help: "Add rules of your own: words that must not be used, topics the agents should not discuss, or numbers that must be hidden.",
      empty: "No guardrails of your own yet.",
      add: "Add a guardrail",
      colName: "Guardrail",
      colLooksFor: "What it looks for",
      colActions: "Change or remove",
      fromOrg: "From the organisation",
      looksFor: {
        phrases: "Words: {{list}}",
        phrasesMore: "Words: {{list}} and {{count}} more",
        topic: "Topic: {{topic}}",
        pattern: "Pattern: {{pattern}}",
      },
      change: "Change {{name}}",
      remove: "Remove {{name}}",
      removeTitle: "Remove the guardrail “{{name}}”? It stops applying when you save the rules.",
      removeOk: "Remove",
      turnOff: "Turn off {{name}} for this agent",
      turnOffTitle: "Turn off “{{name}}” for this agent? The rest of the organisation keeps it.",
      turnOffOk: "Turn off",
      editorAdd: "Add a guardrail",
      editorChange: "Change the guardrail",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      namePlaceholderEn: "For example: Competitors' names",
      namePlaceholderAr: "مثلاً: أسماء المنافسين",
      lookFor: "Look for",
      kind: { phrases: "Words or phrases", topic: "A topic, described in words", pattern: "A pattern (advanced)" },
      phrases: "Words or phrases",
      phrasesHelp: "Press Enter after each one. Arabic and English; capital letters, Arabic marks and letter forms (أ/ا, ة/ه) do not matter. Whole words only.",
      topic: "Describe the topic",
      topicHelp: "In a few words, for example: legal advice, or questions about lawsuits and contracts. An AI model decides whether a message is about it, so it is slower and not exact.",
      pattern: "Pattern",
      patternHelp: "For text that follows a fixed form. Example: CASE-\\d{4}-\\d{6} finds case numbers like CASE-2026-000123. Try it with “Test with sample text” below before saving.",
      onQuestions: "In questions",
      onAnswers: "In answers",
      noHideTopic: "A topic cannot be hidden word by word: choose Stopped or Noted only.",
      ok: "Done",
      added: "Added. Save the rules to apply it.",
      changed: "Changed. Save the rules to apply it.",
      removed: "Removed. Save the rules to apply it.",
      matched: "Your guardrail “{{name}}”",
    },
  },
  ar: {
    intro: "حدد ما يحدث عندما تحتوي رسالة على بيانات شخصية أو أسرار أو محتوى خطر. تُفحص الأسئلة قبل وصولها إلى النموذج، والإجابات قبل وصولها إلى الشخص.",
    scope: "القواعد لـ",
    scopeOrg: "المؤسسة كلها",
    scopeAgent: "الوكيل: {{name}}",
    agentInherits: "يتبع {{name}} قواعد المؤسسة. الحفظ هنا يمنحه قواعده الخاصة.",
    agentOwn: "لـ {{name}} قواعده الخاصة.",
    reset: "استخدام قواعد المؤسسة",
    resetTitle: "إزالة القواعد الخاصة بهذا الوكيل؟ سيعود لاتباع قواعد المؤسسة.",
    resetDone: "عاد {{name}} لاتباع قواعد المؤسسة",
    cancel: "إلغاء",
    presetTitle: "مستوى الحذر",
    preset: { strict: "صارم", balanced: "متوازن", lenient: "مرن", custom: "مخصص" },
    presetHelp: {
      strict: "يلتقط أكثر ما يمكن. يخفي البيانات الشخصية حتى عند الشك ويوقف كل ما يبدو خطراً. قد تتوقف بعض الرسائل غير الضارة.",
      balanced: "موصى به. يخفي البيانات الشخصية والأسرار، ويوقف محاولات خداع الوكيل والمحتوى المسيء.",
      lenient: "مقاطعات أقل. يوقف الحالات الواضحة فقط؛ وتُسجَّل البيانات الشخصية في الأسئلة بدلاً من إخفائها.",
      custom: "غُيّرت هذه القواعد يدوياً (انظر «متقدم»). اختر مستوى لاستبدالها.",
    },
    whatHappens: "ماذا يحدث",
    colWhat: "عندما تحتوي الرسالة على…",
    direction: { input: "الأسئلة", output: "الإجابات" },
    outcome: { block: "يُوقف", redact: "يُخفى", flag: "يُسجَّل فقط", off: "مسموح" },
    category: {
      pii: { name: "بيانات شخصية", explain: "أرقام الهوية والهواتف والبريد الإلكتروني والآيبان وأرقام البطاقات." },
      secret: { name: "أسرار", explain: "كلمات المرور ومفاتيح الواجهات البرمجية ورموز الدخول." },
      prompt_injection: { name: "محاولات خداع الوكيل", explain: "نص يحاول تجاوز تعليمات الوكيل، مثل «تجاهل قواعدك»." },
      unsafe_action: { name: "طلبات خطرة", explain: "طلبات تنفيذ أوامر أو حذف بيانات أو تحويل أموال خارج الخطوات المعتادة." },
      toxicity: { name: "محتوى مسيء", explain: "الشتائم والتهديدات وخطاب الكراهية، بالعربية أو الإنجليزية." },
    },
    advanced: "متقدم: الإجراء والحساسية لكل فئة",
    selfCheck: "فحص إضافي بنموذج ذكاء اصطناعي (أبطأ)",
    colCategory: "الفئة",
    colAction: "الإجراء",
    colThreshold: "الحساسية (0–1)",
    thresholdHelp: "الحساسية هي درجة الثقة المطلوبة قبل تطبيق الإجراء: القيم الأقل تلتقط أكثر، والأعلى تلتقط الحالات الواضحة فقط.",
    save: "حفظ القواعد",
    saveAgent: "حفظ القواعد لـ {{name}}",
    saved: "تم حفظ القواعد",
    history: "التغييرات",
    noHistory: "لا توجد تغييرات بعد: تُطبَّق إعدادات المنصة الافتراضية.",
    colVersion: "الإصدار",
    colBy: "غيّرها",
    colAt: "التاريخ",
    initialSetup: "الإعداد الأولي",
    showUnchanged_zero: "عرض الحفظ الذي لم يغيّر شيئًا ({{count}})",
    showUnchanged_one: "عرض حفظ واحد لم يغيّر شيئًا",
    showUnchanged_two: "عرض حفظين لم يغيّرا شيئًا",
    showUnchanged_few: "عرض {{count}} مرات حفظ لم تغيّر شيئًا",
    showUnchanged_many: "عرض {{count}} مرة حفظ لم تغيّر شيئًا",
    showUnchanged_other: "عرض {{count}} مرة حفظ لم تغيّر شيئًا",
    unchangedAt: "{{at}} (دون تغيير)",
    technical: "تفاصيل تقنية",
    test: {
      title: "اختبار بنص تجريبي",
      help: "شاهد ما ستفعله هذه القواعد (بما فيها التغييرات غير المحفوظة). لا يُخزَّن شيء ولا يُحتفظ بالنص.",
      direction: "افحص النص على أنه",
      as: { input: "سؤال إلى الوكيل", output: "إجابة من الوكيل" },
      sample: "النص التجريبي",
      placeholder: "مثلاً: بريدي sara@example.com ورقم الآيبان SA44 2000 0001 2345 6789 1234",
      run: "اختبار",
      decision: { allow: "سيمر دون تغيير", redact: "سيمر مع إخفاء بعض الأجزاء", block: "سيتم إيقافه" },
      nothing: "لم يُعثر على شيء.",
      found: "ما تم العثور عليه",
      redacted: "ما سيراه النموذج أو الشخص",
    },
    issues: {
      redact_only_pii_secret: "يمكن إخفاء البيانات الشخصية والأسرار فقط؛ اختر «يُوقف» أو «يُسجَّل فقط» أو «مسموح» للبقية.",
      threshold_range: "يجب أن تكون الحساسية بين 0 و1.",
      own_name: "أعطِ الضابط اسماً بالإنجليزية وبالعربية.",
      own_phrases: "أضف كلمة أو عبارة واحدة على الأقل.",
      own_phrases_long: "لكل كلمة أو عبارة 100 حرف على الأكثر، و200 كلمة أو عبارة على الأكثر.",
      own_topic: "صف الموضوع ببضع كلمات (300 حرف على الأكثر).",
      own_pattern: "اكتب نمطاً (200 حرف على الأكثر).",
      own_pattern_invalid: "هذا النمط غير صالح. تحقق من الأقواس والرموز الخاصة.",
      own_pattern_empty: "هذا النمط يطابق نصاً فارغاً أيضاً، لذا سيطابق كل شيء.",
      own_topic_hide: "لا يمكن إخفاء موضوع: اختر «يُوقف» أو «يُسجَّل فقط».",
    },
    own: {
      title: "ضوابطك الخاصة",
      help: "أضف قواعدك الخاصة: كلمات يجب ألا تُستخدم، أو مواضيع يجب ألا يناقشها الوكلاء، أو أرقام يجب إخفاؤها.",
      empty: "لا توجد ضوابط خاصة بك بعد.",
      add: "إضافة ضابط",
      colName: "الضابط",
      colLooksFor: "ما يبحث عنه",
      colActions: "تغيير أو إزالة",
      fromOrg: "من المؤسسة",
      looksFor: {
        phrases: "كلمات: {{list}}",
        phrasesMore: "كلمات: {{list}} و{{count}} غيرها",
        topic: "موضوع: {{topic}}",
        pattern: "نمط: {{pattern}}",
      },
      change: "تغيير {{name}}",
      remove: "إزالة {{name}}",
      removeTitle: "إزالة الضابط «{{name}}»؟ يتوقف تطبيقه عند حفظ القواعد.",
      removeOk: "إزالة",
      turnOff: "إيقاف {{name}} لهذا الوكيل",
      turnOffTitle: "إيقاف «{{name}}» لهذا الوكيل؟ تبقى بقية المؤسسة ملتزمة به.",
      turnOffOk: "إيقاف",
      editorAdd: "إضافة ضابط",
      editorChange: "تغيير الضابط",
      nameEn: "الاسم (بالإنجليزية)",
      nameAr: "الاسم (بالعربية)",
      namePlaceholderEn: "For example: Competitors' names",
      namePlaceholderAr: "مثلاً: أسماء المنافسين",
      lookFor: "ابحث عن",
      kind: { phrases: "كلمات أو عبارات", topic: "موضوع موصوف بالكلمات", pattern: "نمط (متقدم)" },
      phrases: "الكلمات أو العبارات",
      phrasesHelp: "اضغط Enter بعد كل واحدة. بالعربية والإنجليزية؛ لا تهم الأحرف الكبيرة ولا التشكيل ولا أشكال الحروف (أ/ا، ة/ه). كلمات كاملة فقط.",
      topic: "صف الموضوع",
      topicHelp: "ببضع كلمات، مثلاً: الاستشارات القانونية، أو الأسئلة عن الدعاوى والعقود. يقرر نموذج ذكاء اصطناعي ما إذا كانت الرسالة عنه، لذا فهو أبطأ وغير دقيق تماماً.",
      pattern: "النمط",
      patternHelp: "لنص له شكل ثابت. مثال: CASE-\\d{4}-\\d{6} يجد أرقام القضايا مثل CASE-2026-000123. جرّبه بـ«اختبار بنص تجريبي» أدناه قبل الحفظ.",
      onQuestions: "في الأسئلة",
      onAnswers: "في الإجابات",
      noHideTopic: "لا يمكن إخفاء موضوع كلمةً كلمة: اختر «يُوقف» أو «يُسجَّل فقط».",
      ok: "تم",
      added: "أُضيف. احفظ القواعد لتطبيقه.",
      changed: "تغيّر. احفظ القواعد لتطبيقه.",
      removed: "أُزيل. احفظ القواعد لتطبيق ذلك.",
      matched: "ضابطك «{{name}}»",
    },
  },
};
